import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Scale, Copy, Check, BookOpen, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import {
  CalculationResult,
  calcularCompetenciaCuantia,
  calcularPlazoProcesal
} from "@/lib/calculators/calculatorsEngine";
import { CALCULATOR_FAQS } from "@/lib/calculators/calculatorFaqs";
import { deductCalculatorCredit } from "@/lib/calculators/deductCalculatorCredit";

// 1. Competencia y Cuantía
export const CompetenciaForm: React.FC = () => {
  const [pretensiones, setPretensiones] = useState("180000000");
  const [materia, setMateria] = useState<"civil" | "laboral" | "familia" | "administrativo">("civil");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const p = parseFloat(pretensiones) || 0;
    const res = calcularCompetenciaCuantia(p, materia);
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["competencia"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Art. 25 y 26 CGP</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Establece los umbrales de Mínima (≤40 SMLMV), Menor (≤150 SMLMV) y Mayor cuantía (&gt;150 SMLMV).
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">SMLMV 2026</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Parámetro oficial SMLMV ($1.550.000 COP) para determinación automática del fuero.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Instancias Procesales</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Única instancia en mínima cuantía / Primera instancia en menor y mayor cuantía.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Scale className="w-4 h-4 text-violet-600" /> Parámetros del Proceso
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Monto Total de Pretensiones (COP)</Label>
            <Input
              type="number"
              value={pretensiones}
              onChange={(e) => setPretensiones(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Especialidad Jurisdiccional</Label>
            <Select value={materia} onValueChange={(v: any) => setMateria(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="civil">Jurisdicción Civil y Agraria</SelectItem>
                <SelectItem value="laboral">Jurisdicción Laboral y Seguridad Social</SelectItem>
                <SelectItem value="familia">Jurisdicción de Familia</SelectItem>
                <SelectItem value="administrativo">Contencioso Administrativo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-violet-600 hover:bg-violet-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Scale className="w-4 h-4 mr-2" /> Determinar Juez Competente por Cuantía — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <ProcesalResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-violet-600" /> Preguntas Frecuentes de Competencia
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 2. Cómputo de Plazos Procesales
export const PlazosProcesalesForm: React.FC = () => {
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split("T")[0]);
  const [diasTermino, setDiasTermino] = useState("10");
  const [tipoNotificacion, setTipoNotificacion] = useState<"personal" | "estado" | "aviso" | "estrados">("estado");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const d = parseInt(diasTermino) || 1;
    const res = calcularPlazoProcesal(fechaInicio, d, tipoNotificacion);
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["plazos"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Ley 2213 de 2022</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Regula las notificaciones judiciales electrónicas y cómputo de términos por estado.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Exclusión de Inhábiles</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Omite automáticamente sábados, domingos, festivos oficiales de Colombia y vacancia judicial.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Art. 118 CGP</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            El término empieza a correr a partir del día hábil siguiente a la notificación surtida.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-violet-600" /> Parámetros del Término Procesal
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Providencia / Auto</Label>
            <Input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Días del Término (Hábiles)</Label>
            <Input
              type="number"
              value={diasTermino}
              onChange={(e) => setDiasTermino(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Mecanismo de Notificación</Label>
            <Select value={tipoNotificacion} onValueChange={(v: any) => setTipoNotificacion(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="personal">Notificación Personal (Art. 291 CGP)</SelectItem>
                <SelectItem value="estado">Por Estado (Art. 295 CGP)</SelectItem>
                <SelectItem value="aviso">Por Aviso (Art. 292 CGP / Ley 2213)</SelectItem>
                <SelectItem value="estrados">En Estrados (Audiencia)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-violet-600 hover:bg-violet-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calendar className="w-4 h-4 mr-2" /> Calcular Vencimiento de Plazo Hábil — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <ProcesalResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-violet-600" /> Preguntas Frecuentes de Plazos Procesales
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ProcesalResultDisplay: React.FC<{ result: CalculationResult }> = ({ result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `--- INFORME DE PLAZO / COMPETENCIA PROCESAL ---
${result.title}
${result.summary}

RESULTADO: ${result.breakdown.find(b => b.label.includes("Vencimiento") || b.label.includes("Juez"))?.value}

DESGLOSE:
${result.breakdown.map((b) => `- ${b.label}: ${b.value}`).join("\n")}

CITA NORMATIVA:
${result.normativa.map((n) => `- ${n}`).join("\n")}

Generado en Mi Asistente Legalia`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Informe procesal copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 rounded-2xl bg-violet-50/60 border border-violet-200 space-y-6 text-slate-900 shadow-sm animate-in fade-in">
      <div className="flex justify-between items-start">
        <div>
          <span className="text-[11px] font-extrabold text-violet-800 uppercase tracking-wider">
            Resumen de la Determinación Procesal
          </span>
          <div className="text-2xl md:text-3xl font-black text-slate-900 mt-1 capitalize">
            {result.breakdown.find(b => b.label.includes("Vencimiento") || b.label.includes("Juez"))?.value}
          </div>
          <p className="text-xs text-slate-600 mt-1">{result.summary}</p>
        </div>
        <Button onClick={handleCopy} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 font-bold">
          {copied ? <Check className="w-4 h-4 text-violet-600 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copiado!" : "Copiar Informe"}
        </Button>
      </div>

      {/* Breakdown Table */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
          <BookOpen className="w-4 h-4 text-violet-700" /> Desglose del Cómputo
        </h4>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
          {result.breakdown.map((b, i) => (
            <div key={i} className="p-3.5 flex justify-between text-xs">
              <span className="text-slate-600 font-medium">{b.label}</span>
              <span className="font-bold text-slate-900">{b.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Normativa */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2">
        <h5 className="text-xs font-bold text-violet-800 uppercase tracking-wider">Fundamentación Legal (CGP / Ley 2213)</h5>
        <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
          {result.normativa.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};
